import React from 'react';

import { Switch } from '@base-ui/react/switch';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import Color from 'color';
import { Globe, LockKeyhole } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

const ACTIVE_COLOR = Color.rgb(52, 88, 138);
const PUBLIC_TOGGLE_TRACK_COLOR = Color.rgb(21, 76, 151);

const TOGGLE_HEIGHT = 24;
const TOGGLE_WIDTH = 42;
const INDICATOR_SIZE = 16;

const useStyles = makeStyles<{
  isPrivate: boolean;
  roundedTop: boolean;
  borderRadius?: number;
  disabled?: boolean;
}>()((theme, { borderRadius, disabled, isPrivate, roundedTop }) => {
  const toggleMargin = (TOGGLE_HEIGHT - INDICATOR_SIZE) / 2;
  const primary = Color(theme.palette.primary.main);

  return {
    globeActive: {
      color: ACTIVE_COLOR.toString(),
    },
    globeInactive: {
      color: theme.palette.common.white,
    },
    label: {
      color: theme.palette.text.primary,
    },
    lockOnThumb: {
      color: ACTIVE_COLOR.toString(),
    },
    lockOnTrack: {
      color: theme.palette.common.white,
    },
    root: {
      alignItems: 'center',
      backgroundColor: primary.alpha(0.08).toString(),
      borderRadius,
      borderTopLeftRadius: roundedTop ? 3 : 0,
      borderTopRightRadius: roundedTop ? 3 : 0,
      cursor: disabled ? 'default' : 'pointer',
      display: 'flex',
      gap: theme.spacing(1),
      padding: theme.spacing(1, 1.5),
    },
    toggleBar: {
      backgroundColor: PUBLIC_TOGGLE_TRACK_COLOR.alpha(0.3).toString(),
      border: 'none',
      borderRadius: TOGGLE_HEIGHT / 2,
      flexShrink: 0,
      height: TOGGLE_HEIGHT,
      opacity: disabled ? 0.5 : 1,
      padding: 0,
      position: 'relative',
      transition: 'background-color 0.2s ease',
      width: TOGGLE_WIDTH,
    },
    toggleIndicator: {
      alignItems: 'center',
      backgroundColor: theme.palette.common.white,
      borderRadius: '50%',
      display: 'flex',
      height: INDICATOR_SIZE,
      justifyContent: 'center',
      left: isPrivate ? toggleMargin : TOGGLE_WIDTH - INDICATOR_SIZE - toggleMargin,
      position: 'absolute',
      top: toggleMargin,
      transition: 'left 0.2s ease',
      width: INDICATOR_SIZE,
      zIndex: 2,
    },
    trackIconSlot: {
      alignItems: 'center',
      display: 'flex',
      height: '100%',
      justifyContent: 'center',
      pointerEvents: 'none',
      position: 'absolute',
      top: 0,
      width: INDICATOR_SIZE + toggleMargin * 2,
      zIndex: 1,
    },
    trackIconSlotLeft: {
      left: 0,
    },
    trackIconSlotRight: {
      right: 0,
    },
  };
});

interface PrivateCommentToggleProps {
  isPrivate: boolean;
  onChange: (isPrivate: boolean) => void;
  borderRadius?: number;
  disabled?: boolean;
  roundedTop?: boolean;
}

export function PrivateCommentToggle({
  borderRadius,
  disabled = false,
  isPrivate,
  onChange,
  roundedTop = true,
}: PrivateCommentToggleProps): JSX.Element {
  const { classes } = useStyles({ borderRadius, disabled, isPrivate, roundedTop });
  const trackIconSize = TOGGLE_HEIGHT * 0.5;
  const thumbIconSize = TOGGLE_HEIGHT * 0.45;

  return (
    <label className={classes.root}>
      <Switch.Root
        checked={isPrivate}
        className={classes.toggleBar}
        disabled={disabled}
        onCheckedChange={onChange}
      >
        <div className={clsx(classes.trackIconSlot, classes.trackIconSlotLeft)}>
          {!isPrivate && <LockKeyhole className={classes.lockOnTrack} size={trackIconSize} />}
        </div>
        <div className={clsx(classes.trackIconSlot, classes.trackIconSlotRight)}>
          {isPrivate && (
            <Globe className={classes.globeInactive} size={trackIconSize} strokeWidth={2} />
          )}
        </div>
        <Switch.Thumb className={classes.toggleIndicator}>
          {isPrivate ? (
            <LockKeyhole className={classes.lockOnThumb} size={thumbIconSize} />
          ) : (
            <Globe className={classes.globeActive} size={thumbIconSize} strokeWidth={2} />
          )}
        </Switch.Thumb>
      </Switch.Root>
      <Typography className={classes.label} component="span" variant="body2">
        {isPrivate ? 'Visible to organization only' : 'Visible to all parties'}
      </Typography>
    </label>
  );
}
